import { useQuery } from '@tanstack/react-query';

import { useJob } from '~hooks/useJob/useJob';

export const jobLoadsPricingQueryKey = (jobId: string) =>
  ['loadPricing', 'jobLoads', jobId] as const;

/**
 * Loads (with pricing) for one job, for the `load_level_pricing` POC pricing panel.
 *
 * A cascade/divergence re-rates Loads server-side and asynchronously (Trigger E runs in the
 * worker) with no FE-side invalidation, so the cached rates can lag the freshly-fetched pricing
 * history. `refetchOnMount: 'always'` keeps the summary fresh whenever the panel is shown; the
 * race-free freshness signal for the async re-rate is the `CompanyJobUpdateChannel` push handled
 * by `useInvalidateQueriesOnJobUpdate`, so we rely on that rather than overriding the app-wide
 * `refetchOnWindowFocus: false` default.
 */
export default function useJobLoadsPricingQuery(
  jobId: string,
  options?: { enabled?: boolean },
) {
  const { getJobLoads } = useJob();

  return useQuery({
    queryKey: jobLoadsPricingQueryKey(jobId),
    queryFn: ({ signal }) => getJobLoads(jobId, { signal }),
    refetchOnMount: 'always',
    enabled: options?.enabled ?? true,
  });
}
